import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Boxes, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';

interface Pattern {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  category: string;
  blocks: unknown[];
  preview_image: string | null;
  is_global: boolean;
}

export const Route = createFileRoute('/_app/patterns')({
  component: PatternsView,
});

function PatternsView() {
  const qc = useQueryClient();
  const { data: patterns } = useQuery({
    queryKey: ['patterns'],
    queryFn: () => unwrap<Pattern[]>(api.get('/patterns')),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/patterns/${id}`),
    onSuccess: () => {
      toast.success('Patrón eliminado');
      void qc.invalidateQueries({ queryKey: ['patterns'] });
    },
  });

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-8">
      <header className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Patrones</h1>
        <p className="text-sm text-neutral-500">
          Conjuntos de bloques reutilizables. Guarda combinaciones que uses a menudo desde el editor (próximamente: botón "Guardar como patrón" en cada bloque).
        </p>
      </header>

      {(patterns ?? []).length === 0 && (
        <Card>
          <CardContent className="py-16 text-center text-sm text-neutral-500">
            <Boxes className="mx-auto mb-2 size-8 text-neutral-300" />
            Aún no tienes patrones guardados.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {(patterns ?? []).map((p) => (
          <Card key={p.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <CardTitle className="text-base">{p.name}</CardTitle>
                <Badge variant="outline" className="text-[10px]">{p.category}</Badge>
              </div>
              <CardDescription>{p.description ?? `${p.blocks.length} bloques`}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between gap-2">
                <div className="text-xs text-neutral-500">
                  {p.blocks.length} bloques · {p.is_global ? 'Compartido' : 'Privado'}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-2 text-red-600 hover:bg-red-50"
                  onClick={() => {
                    if (confirm('¿Eliminar patrón?')) remove.mutate(p.id);
                  }}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
